import React, { useEffect, useState } from "react";
import { api } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { FileSignature, Download, DollarSign, CheckCircle2, Clock } from "lucide-react";
import { DealerInfoGrid } from "@/components/DealerInfoGrid";
import { ReadinessFlags } from "@/components/ReadinessFlags";

const money = (v) => (v == null || v === "" ? "—" : `$${Number(v).toFixed(2)}`);
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString() : null);

export default function DealerFilePanel({ onboardingId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/onboarding/${onboardingId}/dealer-file`).then(({ data }) => setData(data)).catch(() => {});
  }, [onboardingId]);
  if (!data) return null;
  const d = data.dealer;
  const c = data.commissions || {};
  const download = async () => {
    try {
      const { data: blob } = await api.get(`/dealers/${d.id}/executed.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `${d.dealer_name}-executed.pdf`; a.click();
    } catch (e) { toast.error(e.response?.status === 404 ? "No executed PDF yet" : "Download failed"); }
  };
  return (
    <Card className="p-5 border-[#E9ECEF] rounded-sm mb-4 bg-white" data-testid="dealer-file-panel">
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#D4AF37] mb-3 flex items-center justify-between">Dealer file — follows this dealer through every stage
        {d && <Link to={`/dealers/${d.id}/profile`} className="normal-case tracking-normal text-[11px] font-semibold text-[#0B132B] underline" data-testid="dealer-file-profile-link">Open dealer profile →</Link>}</div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div data-testid="dealer-file-agreement">
          <div className="flex items-center gap-2 text-sm font-bold text-[#0B132B] mb-2"><FileSignature size={14} /> Dealer agreement</div>
          {d ? (
            <>
              <div className="text-sm text-[#0B132B]">{d.dealer_name}{d.dba ? <span className="text-[#6C757D]"> · dba {d.dba}</span> : null}</div>
              <div className="text-xs text-[#6C757D]">{d.physical_address || ""}</div>
              <div className="text-xs text-[#6C757D]">{d.contact_name}{d.contact_email ? ` · ${d.contact_email}` : ""}{d.contact_phone ? ` · ${d.contact_phone}` : ""}</div>
              <ul className="mt-2 space-y-1">
                {(d.forms || []).map((f) => (
                  <li key={f.id} className="flex items-center gap-2 text-xs" data-testid={`dealer-file-form-${f.id}`}>
                    {f.signed ? <CheckCircle2 size={12} className="text-emerald-600" /> : <Clock size={12} className="text-amber-500" />}
                    <span className="text-[#0B132B]">{f.name}</span><span className="text-[#6C757D]">· {f.signed ? "signed" : "awaiting signature"}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-2 text-xs text-[#6C757D]">
                {d.signed_at ? `Signed ${fmt(d.signed_at)}${d.signer_name ? ` by ${d.signer_name}` : ""}` : "Not signed yet"}
                {d.countersigned_at ? ` · Countersigned ${fmt(d.countersigned_at)}` : ""}
              </div>
              <Button size="sm" variant="outline" onClick={download} disabled={!d.has_executed_pdf} className="mt-2 h-8 rounded-sm border-[#0B132B]/20 text-xs" data-testid="dealer-file-download-executed">
                <Download size={12} className="mr-1" /> {d.has_executed_pdf ? "Download executed agreement" : "Executed PDF pending"}
              </Button>
              <DealerInfoGrid d={d} />
              {data.readiness && <div className="mt-3 border-t border-[#F1F3F5] pt-3"><ReadinessFlags flags={data.readiness} compact /></div>}
            </>
          ) : <div className="text-xs text-[#6C757D]">No dealer record linked.</div>}
        </div>
        <div data-testid="dealer-file-commissions">
          <div className="flex items-center gap-2 text-sm font-bold text-[#0B132B] mb-2"><DollarSign size={14} /> Commission structure</div>
          <div className="text-xs text-[#6C757D] mb-2">{c.signing_agent_name || "—"}{c.agency ? ` · ${c.agency}` : ""}{c.commissions_review_status ? ` · review: ${c.commissions_review_status.replace("_", " ")}` : ""}</div>
          <table className="w-full text-left text-xs">
            <thead><tr className="text-[10px] uppercase tracking-[0.14em] text-[#6C757D] border-b border-[#E9ECEF]"><th className="py-1 pr-2">Program</th><th className="py-1 pr-2">Over-remit</th><th className="py-1">Agent comm.</th></tr></thead>
            <tbody>
              {(c.products || []).map((p, i) => (
                <tr key={i} className="border-b border-[#F1F3F5]"><td className="py-1.5 pr-2 text-[#0B132B] font-semibold">{p.product_name} <span className="text-[#6C757D] font-normal">· {p.vertical_key}</span>
                  {(p.vsc_programs || []).length > 0 && <div className="text-[10px] text-[#D4AF37] font-bold uppercase tracking-[0.12em]">{p.vsc_programs.map((k) => ({ new_car: "New Car", preowned: "Pre-Owned", blended: "Blended" }[k] || k)).join(" · ")}</div>}
                </td><td className="py-1.5 pr-2">{money(p.dealer_over_remit)}</td><td className="py-1.5">{money(p.agent_commission)}</td></tr>
              ))}
            </tbody>
          </table>
          <div className="mt-2 flex gap-4 text-xs text-[#6C757D]">
            <span>Subagent markup: <b className="text-[#0B132B]">{money(c.subagent_commission_markup)}</b></span>
            <span>Agency commission: <b className="text-[#0B132B]">{money(c.agency_commission)}</b></span>
          </div>
        </div>
      </div>
    </Card>
  );
}
